import { LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { postReaderStyles } from './post-reader.css';
import { renderPostReaderPresentation, type PostReaderProps } from './post-reader-presentation';
import { MarkdownDoc } from '../../data/markdownEngine';

@customElement('post-reader')
export class PostReader extends LitElement {
  static styles = [postReaderStyles];

  @property({ type: Object }) doc: MarkdownDoc | null = null;

  @state() private unlocked = false;
  @state() private passwordInput = '';
  @state() private showPassword = false;
  @state() private hasError = false;

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('doc')) {
      this.unlocked = !this.doc?.password;
      this.passwordInput = '';
      this.showPassword = false;
      this.hasError = false;
    }
  }

  private handleBackToList() {
    this.dispatchEvent(
      new CustomEvent('back-to-list', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleToggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private handlePasswordInput(value: string) {
    this.passwordInput = value;

    if (!this.doc?.password) {
      this.unlocked = true;
      this.hasError = false;
      return;
    }

    const expected = this.doc.password.trim();
    const actual = value.trim();

    if (actual === expected) {
      this.unlocked = true;
      this.hasError = false;
    } else {
      this.hasError = false;
    }
  }

  private handleUnlockSubmit() {
    if (!this.doc?.password) {
      this.unlocked = true;
      this.hasError = false;
      return;
    }

    const expected = this.doc.password.trim();
    const actual = this.passwordInput.trim();

    if (actual === expected) {
      this.unlocked = true;
      this.hasError = false;
    } else {
      this.hasError = true;
    }
  }

  render() {
    const isUnlocked = !this.doc?.password || this.unlocked;

    const props: PostReaderProps = {
      doc: this.doc,
      isUnlocked,
      passwordInput: this.passwordInput,
      showPassword: this.showPassword,
      hasError: this.hasError,
      onBackToList: () => this.handleBackToList(),
      onPasswordInput: (val: string) => this.handlePasswordInput(val),
      onToggleShowPassword: () => this.handleToggleShowPassword(),
      onUnlockSubmit: () => this.handleUnlockSubmit(),
    };

    return renderPostReaderPresentation(props);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'post-reader': PostReader;
  }
}
